import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Textarea } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import type { CalendarEntry, CalendarEntryCreate } from '../../types';

interface CalendarEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: CalendarEntry | null;
  defaultDate?: string;
  onSuccess: (year: number, month: number) => void;
}

const emptyForm = (): CalendarEntryCreate => ({
  date_start: '',
  date_end: '',
  license_plate: '',
  renter_name: '',
  renter_phone: '',
  price_cents: undefined,
  notes: '',
  entry_type: 'freetext',
});

export function CalendarEntryDialog({
  open,
  onOpenChange,
  entry,
  defaultDate,
  onSuccess,
}: CalendarEntryDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!entry;

  const [formData, setFormData] = useState<CalendarEntryCreate>(emptyForm());
  const [priceInput, setPriceInput] = useState('');

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setFormData({
        date_start: entry.date_start,
        date_end: entry.date_end,
        license_plate: entry.license_plate,
        renter_name: entry.renter_name,
        renter_phone: entry.renter_phone ?? '',
        price_cents: entry.price_cents ?? undefined,
        notes: entry.notes ?? '',
        entry_type: entry.entry_type,
      });
      setPriceInput(entry.price_cents != null ? (entry.price_cents / 100).toFixed(2) : '');
    } else {
      const d = emptyForm();
      if (defaultDate) {
        d.date_start = defaultDate;
        d.date_end = defaultDate;
      }
      setFormData(d);
      setPriceInput('');
    }
  }, [open, entry, defaultDate]);

  const mutation = useMutation({
    mutationFn: async (data: CalendarEntryCreate) => {
      if (isEdit) {
        return api.put(`/calendar/${entry!.id}`, data);
      }
      return api.post('/calendar', data);
    },
    onSuccess: (_, data) => {
      const [year, month] = data.date_start.split('-').map(Number);
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast({ title: isEdit ? 'Eintrag aktualisiert' : 'Eintrag erstellt' });
      onOpenChange(false);
      onSuccess(year, month);
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Eintrag konnte nicht gespeichert werden',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/calendar/${entry!.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast({ title: 'Eintrag gelöscht' });
      onOpenChange(false);
      const [year, month] = entry!.date_start.split('-').map(Number);
      onSuccess(year, month);
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Eintrag konnte nicht gelöscht werden',
        variant: 'destructive',
      });
    },
  });

  const handleChange = (field: keyof CalendarEntryCreate, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePriceChange = (value: string) => {
    setPriceInput(value);
    const parsed = parseFloat(value.replace(',', '.'));
    setFormData((prev) => ({
      ...prev,
      price_cents: isNaN(parsed) ? undefined : Math.round(parsed * 100),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.date_start || !formData.date_end) {
      toast({ title: 'Bitte Start- und Enddatum angeben', variant: 'destructive' });
      return;
    }
    if (!formData.license_plate.trim()) {
      toast({ title: 'Bitte Kennzeichen angeben', variant: 'destructive' });
      return;
    }
    if (!formData.renter_name.trim()) {
      toast({ title: 'Bitte Mieter-Name angeben', variant: 'destructive' });
      return;
    }
    mutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? 'Eintrag bearbeiten' : 'Neuer Kalendereintrag'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="date_start">Startdatum *</Label>
              <Input
                id="date_start"
                type="date"
                value={formData.date_start}
                onChange={(e) => handleChange('date_start', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="date_end">Enddatum *</Label>
              <Input
                id="date_end"
                type="date"
                value={formData.date_end}
                min={formData.date_start}
                onChange={(e) => handleChange('date_end', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="license_plate">Kennzeichen *</Label>
            <Input
              id="license_plate"
              placeholder="z.B. MYK-AB 123"
              value={formData.license_plate}
              onChange={(e) => handleChange('license_plate', e.target.value.toUpperCase())}
              maxLength={20}
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="renter_name">Mieter-Name *</Label>
            <Input
              id="renter_name"
              placeholder="Vor- und Nachname"
              value={formData.renter_name}
              onChange={(e) => handleChange('renter_name', e.target.value)}
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="renter_phone">Telefonnummer</Label>
            <Input
              id="renter_phone"
              type="tel"
              placeholder="+49 123 456789"
              value={formData.renter_phone ?? ''}
              onChange={(e) => handleChange('renter_phone', e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="price">Preis (€)</Label>
            <Input
              id="price"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={priceInput}
              onChange={(e) => handlePriceChange(e.target.value)}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              placeholder="Weitere Informationen..."
              value={formData.notes ?? ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-between pt-2">
            <div>
              {isEdit && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                >
                  Löschen
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {isEdit ? 'Speichern' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
